import { useState, useCallback } from "react";

const useCampaigns = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL;
  /** Backend: @Controller('/api/v1/campaigns') + admin routes */
  const CAMPAIGN_ADMIN_BASE = `${API_BASE_URL}/campaigns/admin`;
  const accessToken = localStorage.getItem("accessToken");

  const fetchData = useCallback(
    async (url, options = {}) => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            ...options.headers,
          },
          ...options,
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.message || response.statusText);
        }
        return await response.json();
      } catch (err) {
        setError(err.message || "An unexpected error occurred");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );

  /** List campaigns: GET /api/v1/campaigns/admin?page=1&limit=10&status=&name= */
  const fetchCampaigns = async (params = {}) => {
    const url = new URL(CAMPAIGN_ADMIN_BASE);
    Object.entries(params).forEach(([key, value]) => {
      if (value != null && value !== "") url.searchParams.append(key, value);
    });
    const result = await fetchData(url);
    return result; // { data: Campaign[], meta: { total, page, limit, totalPages } }
  };

  /** Get one campaign: GET /api/v1/campaigns/admin/:id */
  const fetchCampaignById = async (id) => {
    const result = await fetchData(`${CAMPAIGN_ADMIN_BASE}/${id}`);
    return result;
  };

  /** Create campaign: POST /api/v1/campaigns/admin body: { name, startDate, endDate, couponCode } */
  const submitCampaign = async (formData) => {
    return await fetchData(CAMPAIGN_ADMIN_BASE, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(formData),
    });
  };

  /** Update campaign: PUT /api/v1/campaigns/admin/:id body: { name?, startDate?, endDate?, couponCode?, isActive? } */
  const updateCampaign = async (campaignId, data) => {
    return await fetchData(`${CAMPAIGN_ADMIN_BASE}/${campaignId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });
  };

  /** Soft delete: DELETE /api/v1/campaigns/admin/:id */
  const deleteCampaign = async (campaignId) => {
    return await fetchData(`${CAMPAIGN_ADMIN_BASE}/${campaignId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  };

  /**
   * Download campaign redemptions CSV: GET /api/v1/campaigns/admin/:id/redemptions/csv
   */
  const downloadCampaignReportCsv = async (campaignId, campaignName = "campaign") => {
    try {
      const response = await fetch(
        `${CAMPAIGN_ADMIN_BASE}/${campaignId}/redemptions/csv`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (!response.ok) throw new Error(response.statusText || "Download failed");
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition");
      const filenameMatch = disposition?.match(/filename="?([^";\n]+)"?/);
      const filename = filenameMatch
        ? filenameMatch[1].trim()
        : `${campaignName}-redemptions.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return true;
    } catch (err) {
      setError(err.message || "Failed to download report");
      return false;
    }
  };

  return {
    loading,
    error,
    fetchCampaigns,
    fetchCampaignById,
    submitCampaign,
    updateCampaign,
    deleteCampaign,
    downloadCampaignReportCsv,
  };
};

export default useCampaigns;
